/**
 * sanitizeHtml — client-side HTML sanitizer for any site that renders
 * user-provided or AI-provided strings through `dangerouslySetInnerHTML`.
 *
 * Thin wrapper around DOMPurify so the call sites don't care about the
 * library or its configuration. Two flavours:
 *
 *   sanitizeHtml(raw)        — default allowlist (bold/italic/lists/links/
 *                               paragraphs/br/headings/tables). Good for
 *                               announcement bodies, AI markdown output,
 *                               email template previews.
 *
 *   sanitizeBasicFormat(raw) — strictly inline formatting + line breaks
 *                               (b, i, strong, em, br). Good for address
 *                               blocks, short notes — zero risk of
 *                               embedded blocks.
 *
 * Both strip <script>, inline event handlers (onerror/onclick/…), style
 * attributes, object/embed/iframe, and any attribute that starts with
 * `on*`. DOMPurify defaults cover the common XSS vectors; we override the
 * allowlist to be conservative.
 *
 * Server-side sanitization still happens on write for announcements via
 * `sanitize-html` in the API. This client-side pass is defense-in-depth
 * for anything else we render.
 *
 * @module utils/sanitizeHtml
 */

import DOMPurify from "dompurify";

/** Rich formatting — tables, headings, links, emphasis, lists, paragraphs. */
const RICH_CONFIG = {
  ALLOWED_TAGS: [
    "a", "b", "blockquote", "br", "code", "div", "em", "h1", "h2",
    "h3", "h4", "h5", "h6", "hr", "i", "img", "li", "ol", "p", "pre",
    "span", "strong", "sub", "sup", "table", "tbody", "td", "tfoot",
    "th", "thead", "tr", "u", "ul",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title", "colspan", "rowspan"],
  // Force `rel="noopener noreferrer"` on every <a> so a malicious link
  // can't window.opener-hijack the tab.
  ADD_ATTR: ["target"],
  ALLOW_DATA_ATTR: false,
  FORBID_ATTR: ["style", "on*"],
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "style"],
};

/** Inline only — bold/italic/line-break/anchors. No block elements. */
const BASIC_CONFIG = {
  ALLOWED_TAGS: ["b", "i", "strong", "em", "br", "u", "a"],
  ALLOWED_ATTR: ["href", "target", "rel"],
  ALLOW_DATA_ATTR: false,
  FORBID_ATTR: ["style", "on*"],
  FORBID_TAGS: ["script", "iframe", "object", "embed", "style"],
};

/**
 * Hook DOMPurify so every <a> gets `rel="noopener noreferrer"` and
 * `target="_blank"` — prevents window.opener hijacking on links we render.
 */
if (typeof DOMPurify.addHook === "function") {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("rel", "noopener noreferrer");
      if (!node.getAttribute("target")) {
        node.setAttribute("target", "_blank");
      }
    }
  });
}

/**
 * Sanitize HTML with the "rich" allowlist. Use on announcement bodies,
 * email template previews, AI markdown output.
 */
export function sanitizeHtml(raw) {
  if (raw == null) return "";
  return DOMPurify.sanitize(String(raw), RICH_CONFIG);
}

/**
 * Sanitize HTML with the "basic" allowlist (inline formatting only).
 * Use on address blocks, short captions.
 */
export function sanitizeBasicFormat(raw) {
  if (raw == null) return "";
  return DOMPurify.sanitize(String(raw), BASIC_CONFIG);
}

export default sanitizeHtml;
